import type { ReactNode } from "react";
import { SystemIllustration } from "./SystemIllustration";

/**
 * The centred message card both OFFLINE and 404 are drawn with (design: the
 * `sysMsg` block): a soft blob, 220px art, a small eyebrow, a 24px title, a
 * 15px sentence and the actions.
 *
 * `flow="overlay"` covers the shell's main area (offline: a state of the whole
 * app, over a page that stays mounted underneath); `flow="page"` sits in the
 * page like any route (404: it IS the route's content).
 */
export function SystemMessage({
  kind,
  flow,
  eyebrow,
  title,
  description,
  actions,
  note,
  testId,
  headingRef,
  role,
}: {
  kind: "offline" | "notFound";
  flow: "overlay" | "page";
  eyebrow?: ReactNode;
  title: string;
  description: string;
  actions: ReactNode;
  note?: ReactNode;
  testId: string;
  headingRef?: React.Ref<HTMLHeadingElement>;
  /** `alert` for offline: announced once when it opens. */
  role?: "alert";
}) {
  return (
    <div
      data-ui="system-pane"
      data-kind={kind}
      data-flow={flow}
      data-testid={testId}
      role={role}
    >
      <div data-ui="system-pane-body">
        <div data-ui="system-blob" aria-hidden="true" />
        <div data-ui="system-card">
          <div data-ui="system-art">
            <SystemIllustration art={kind} />
          </div>
          {eyebrow}
          <h1 data-ui="system-title" ref={headingRef} tabIndex={-1}>
            {title}
          </h1>
          <p data-ui="system-sub">{description}</p>
          <div data-ui="system-actions">{actions}</div>
          {note}
        </div>
      </div>
    </div>
  );
}
